import { getInstructorStats } from '@/api/lessons/lessonApi';
import { formatCurrency, formatDateTime } from '@/components/home/format';
import { MetricCard } from '@/components/home/MetricCard';
import type { InstructorHomeSummary, LessonSummary } from '@/types/lesson.model';
import { handleGlobalError } from '@/utils/error-handler';
import {
  CalendarDays,
  DollarSign,
  GraduationCap,
  TrendingUp,
  Users,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const LessonsSummarySection: React.FC = () => {
  const [stats, setStats] = useState<InstructorHomeSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setStats(await getInstructorStats());
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'No se pudieron cargar tus estadísticas de clases. Por favor, intentá nuevamente más tarde.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const upcoming: LessonSummary[] = stats?.upcomingLessons ?? [];

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[16px] font-bold text-gc-ink">Clases</h2>
      {loading ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-[14px] border border-gc-line bg-gray-100"
              />
            ))}
          </div>
          <div className="h-40 animate-pulse rounded-[14px] border border-gc-line bg-gray-100" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricCard
              icon={DollarSign}
              label="Ingresos del mes"
              value={formatCurrency(stats?.monthlyRevenue ?? 0)}
            />
            <MetricCard
              icon={Users}
              label="Alumnos inscriptos"
              value={String(stats?.totalStudents ?? 0)}
            />
            <MetricCard
              icon={GraduationCap}
              label="Clases dictadas"
              value={String(stats?.lessonsTaught ?? 0)}
            />
            <MetricCard
              icon={TrendingUp}
              label="Promedio por clase"
              value={(stats?.averageStudentsPerLesson ?? 0).toFixed(1)}
            />
          </div>

          <div className="rounded-[14px] border border-gc-line bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gc-blue" />
              <h3 className="text-[15px] font-bold text-gc-ink">
                Próximas clases
              </h3>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-[13.5px] text-gc-muted">
                Todavía no tenés clases próximas.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-gc-line">
                {upcoming.map((lesson) => (
                  <li
                    key={lesson.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="flex flex-col">
                      <span className="text-[14px] font-medium text-gc-ink">
                        {lesson.sport?.name ?? 'Clase'}
                        {lesson.club?.name ? ` · ${lesson.club.name}` : ''}
                      </span>
                      <span className="text-[12.5px] text-gc-muted">
                        {formatDateTime(lesson.startDate)}
                      </span>
                    </div>
                    <span className="whitespace-nowrap text-[12.5px] text-gc-muted">
                      {(lesson.players?.length ?? 0)}/
                      {lesson.maxPlayerLesson ?? 0} alumnos
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
};
